import React, { useRef, useMemo, useState, useEffect, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { POS, TOWER } from "./layout";
import { makeFacade, makePaving } from "../lib/textures";
import { fadeGroup, towerOpacity, sphereOpacity } from "./fade";
import { envState } from "./env";
import { useStore } from "../store";

const readTemporal = () => useStore.getState().temporal;

function FountainWater() {
  const ref = useRef();
  const geo = useMemo(() => new THREE.PlaneGeometry(37, 37, 24, 24), []);
  const base = useMemo(() => geo.attributes.position.array.slice(), [geo]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const p = geo.attributes.position;
    for (let i = 0; i < p.count; i++) {
      const x = base[i * 3];
      const y = base[i * 3 + 1];
      p.array[i * 3 + 2] = Math.sin(x * 0.6 + t * 3) * 0.12 + Math.cos(y * 0.7 + t * 2.4) * 0.1;
    }
    p.needsUpdate = true;
  });
  return (
    <mesh ref={ref} geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.85, 0]}>
      <meshStandardMaterial color="#d2d7d9" metalness={0.9} roughness={0.2} transparent opacity={0.5} emissive="#1a2226" emissiveIntensity={0.08} envMapIntensity={1.4} />
    </mesh>
  );
}

function SphereDataNode() {
  const [open, setOpen] = useState(false);
  return (
    <group position={[0, 12, 0]}>
      <mesh onClick={() => setOpen((o) => !o)}>
        <sphereGeometry args={[0.8, 16, 16]} />
        <meshStandardMaterial color="#D4AF37" emissive="#D4AF37" emissiveIntensity={0.8} />
      </mesh>
      <Html center distanceFactor={90} position={[0, 3, 0]} zIndexRange={[20, 0]}>
        <div
          data-testid="sphere-data-node"
          onClick={() => setOpen((o) => !o)}
          className="cursor-pointer select-none"
          style={{ transform: "translate(-50%,-50%)" }}
        >
          {!open ? (
            <div className="glass-dark rounded-full px-3 py-1 text-[10px] tracking-widest text-[#F4E3A1] font-mono-tele whitespace-nowrap border border-[#D4AF37]/50 shadow-lg">
              ✦ THE SPHERE — TAP FOR HISTORY
            </div>
          ) : (
            <div className="glass-dark rounded-2xl p-4 w-64 text-left border border-[#D4AF37]/40 shadow-2xl">
              <div className="font-serif-display text-[#F4E3A1] text-lg leading-tight">The Sphere</div>
              <div className="font-mono-tele text-[8px] tracking-widest text-stone-400 uppercase mb-2">
                Fritz Koenig · 1971 · Große Kugelkaryatide
              </div>
              <p className="text-[11px] text-stone-200 leading-relaxed">
                A 25-ton bronze sculpture that stood at the center of Austin J. Tobin Plaza for
                three decades. Recovered damaged from the rubble, it became a symbol of resilience
                and now stands in Liberty Park as a memorial.
              </p>
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

function useTwinMat() {
  return useMemo(() => {
    const { map, emissiveMap } = makeFacade("twin");
    map.repeat.set(3, 7);
    emissiveMap.repeat.set(3, 7);
    return new THREE.MeshStandardMaterial({
      map,
      emissiveMap,
      emissive: new THREE.Color("#ffcf87"),
      emissiveIntensity: 0,
      color: "#dfe3e5",
      metalness: 0.9,
      roughness: 0.2,
      envMapIntensity: 1.4,
    });
  }, []);
}

// Yamasaki trident colonnade: pointed aluminium ribs around the tower base.
function tridentMatrices() {
  const ribs = [];
  const caps = [];
  const w = TOWER.footprint;
  const half = w / 2;
  const ribH = 26;
  const step = 2.6;
  const nPer = Math.floor(w / step);
  [POS.northPool, POS.southPool].forEach(([cx, , cz]) => {
    for (let side = 0; side < 4; side++) {
      for (let i = 0; i < nPer; i++) {
        const o = -half + step / 2 + i * step;
        let x = cx,
          z = cz,
          rot = 0;
        if (side === 0) {
          x = cx + o;
          z = cz + half;
        } else if (side === 1) {
          x = cx + o;
          z = cz - half;
        } else if (side === 2) {
          x = cx + half;
          z = cz + o;
          rot = Math.PI / 2;
        } else {
          x = cx - half;
          z = cz + o;
          rot = Math.PI / 2;
        }
        ribs.push({ x, z, rot, y: ribH / 2 });
        caps.push({ x, z, rot, y: ribH });
      }
    }
  });
  return { ribs, caps };
}

export default function TwinTowers() {
  const group = useRef();
  const sphereGroup = useRef();
  const plazaFountainGroup = useRef();
  const mat = useTwinMat();
  const ribRef = useRef();
  const capRef = useRef();
  const sphereRef = useRef();
  const flameRef = useRef();
  const { ribs, caps } = useMemo(() => tridentMatrices(), []);
  const paving = useMemo(() => makePaving(), []);
  const plazaVec = useMemo(() => new THREE.Vector3(...POS.sphere), []);
  const libertyVec = useMemo(() => new THREE.Vector3(150, 0, 120), []);

  useLayoutEffect(() => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    if (ribRef.current) {
      ribs.forEach((r, i) => {
        q.setFromAxisAngle(up, r.rot);
        m.compose(new THREE.Vector3(r.x, r.y, r.z), q, new THREE.Vector3(1, 1, 1));
        ribRef.current.setMatrixAt(i, m);
      });
      ribRef.current.instanceMatrix.needsUpdate = true;
    }
    if (capRef.current) {
      caps.forEach((r, i) => {
        q.setFromAxisAngle(up, r.rot);
        m.compose(new THREE.Vector3(r.x, r.y, r.z), q, new THREE.Vector3(1, 1, 1));
        capRef.current.setMatrixAt(i, m);
      });
      capRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [ribs, caps]);

  useFrame((state) => {
    const t = readTemporal();
    const op = towerOpacity(t);
    mat.emissiveIntensity = envState.night * 1.9;
    fadeGroup(group.current, op);
    fadeGroup(plazaFountainGroup.current, sphereOpacity(t));
    // Sphere survives: it relocates from the plaza to Liberty Park by the 2026 era.
    if (sphereGroup.current) {
      const k = Math.max(0, Math.min(1, (t - 2004) / 10));
      sphereGroup.current.position.lerpVectors(plazaVec, libertyVec, k);
    }
    if (sphereRef.current)
      sphereRef.current.rotation.y = state.clock.elapsedTime * 0.15;
    if (flameRef.current)
      flameRef.current.intensity = 0.5 + envState.night * 4.5;
  });

  const H = TOWER.height;
  const fp = TOWER.footprint;

  return (
    <>
      <group ref={group}>
        {[POS.northPool, POS.southPool].map(([x, , z], idx) => (
          <group key={idx} position={[x, 0, z]}>
            <mesh position={[0, H / 2, 0]} castShadow receiveShadow material={mat}>
              <boxGeometry args={[fp, H, fp]} />
            </mesh>
            {/* roof mechanical block */}
            <mesh position={[0, H + 3, 0]} castShadow>
              <boxGeometry args={[fp * 0.85, 6, fp * 0.85]} />
              <meshStandardMaterial color="#6c7075" metalness={0.6} roughness={0.5} />
            </mesh>
            {/* North Tower antenna */}
            {idx === 0 && (
              <mesh position={[0, H + 60, 0]} castShadow>
                <cylinderGeometry args={[0.7, 1.4, 110, 12]} />
                <meshStandardMaterial color="#8a8f94" metalness={0.8} roughness={0.35} />
              </mesh>
            )}
          </group>
        ))}

        {/* Trident colonnade */}
        <instancedMesh ref={ribRef} args={[null, null, ribs.length]} castShadow receiveShadow>
          <boxGeometry args={[1.0, 26, 0.7]} />
          <meshStandardMaterial color="#d3d7d9" metalness={0.7} roughness={0.3} />
        </instancedMesh>
        <instancedMesh ref={capRef} args={[null, null, caps.length]} castShadow>
          <coneGeometry args={[0.85, 6, 4]} />
          <meshStandardMaterial color="#dfe3e4" metalness={0.7} roughness={0.28} />
        </instancedMesh>
      </group>

      {/* Austin J. Tobin Plaza fountain & concentric paving (2001 only) */}
      <group ref={plazaFountainGroup} position={POS.sphere}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.07, 0]} receiveShadow>
          <circleGeometry args={[62, 96]} />
          <meshStandardMaterial map={paving} roughness={0.9} metalness={0.05} />
        </mesh>
        <mesh position={[0, 0.15, 0]} receiveShadow>
          <cylinderGeometry args={[22, 22, 0.4, 64]} />
          <meshStandardMaterial color="#4a4f55" metalness={0.2} roughness={0.6} />
        </mesh>
        <mesh position={[0, 0.9, 0]}>
          <cylinderGeometry args={[22.6, 22.6, 1.4, 64, 1, true]} />
          <meshStandardMaterial color="#6b7075" metalness={0.5} roughness={0.4} side={THREE.DoubleSide} />
        </mesh>
        <FountainWater />
        <mesh position={[0, 1.6, 0]}>
          <cylinderGeometry args={[2.2, 2.6, 1.6, 24]} />
          <meshStandardMaterial color="#4a3a1e" metalness={0.6} roughness={0.6} />
        </mesh>
      </group>

      {/* The Sphere sculpture — survives & relocates to Liberty Park by the present era */}
      <group ref={sphereGroup} position={POS.sphere}>
        <mesh position={[0, 1.6, 0]}>
          <cylinderGeometry args={[2.2, 2.6, 1.6, 24]} />
          <meshStandardMaterial color="#4a3a1e" metalness={0.6} roughness={0.6} />
        </mesh>
        <mesh ref={sphereRef} position={[0, 7.7, 0]} castShadow renderOrder={5}>
          <sphereGeometry args={[4.6, 64, 64]} />
          <meshStandardMaterial
            color="#9a7530"
            metalness={0.6}
            roughness={0.5}
            emissive="#3a2a0c"
            emissiveIntensity={0.3}
            envMapIntensity={1.1}
            depthTest={true}
          />
        </mesh>
        <SphereDataNode />
        {/* memorial spotlight + eternal-flame glow */}
        <spotLight position={[0, 34, 0]} angle={0.5} penumbra={0.6} intensity={2.2} distance={70} color="#ffdca0" target-position={[0, 6, 0]} />
        <pointLight ref={flameRef} position={[0, 12, 0]} color="#ffb860" intensity={0.6} distance={80} decay={1.5} />
        <mesh position={[0, 11.4, 0]}>
          <sphereGeometry args={[0.7, 12, 12]} />
          <meshBasicMaterial color="#ffcf7a" transparent opacity={0.9} />
        </mesh>
      </group>
    </>
  );
}
